import { Module, ActionTree, MutationTree, GetterTree } from "vuex"
import { RootState } from "@/store/RootStore"
import { make } from "vuex-pathify"
import { Property, PublicPropertyInfo, PropertyAccessOption, DateUtils } from "rp-components"
import { StoreUtils } from "@/utils/StoreUtils"
import Services from "@/services/Services"

const namespaced: boolean = true

//===================================
// State
//===================================

export interface PropertiesState {
    property?: Property
    publicPropertyInfo?: PublicPropertyInfo
    properties?: Property[]
    propertiesAccesses?: PropertyAccessOption[]
}

export const state: PropertiesState = {
    property: undefined,
    publicPropertyInfo: undefined,
    properties: undefined,
    propertiesAccesses: undefined
}

//===================================
// Getters
//===================================
const getters: GetterTree<PropertiesState, RootState> = {
    ...make.getters(state),

    propertyTimeZoneFormatted(state) {
        return DateUtils.timeZoneLongName(state.property?.timeZoneId)
    }
}

//===================================
// Actions
//===================================
export const actions: ActionTree<PropertiesState, RootState> = {
    ...make.actions(state),

    resetState({ commit }) {
        commit("resetState")
    },

    async loadProperties({ dispatch }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const properties = await Services.property.getProperties()
            dispatch("setProperties", properties)
        })
    },

    async loadProperty({ state, dispatch }, propertyId: number) {
        if (state.property?.id != propertyId) {
            await StoreUtils.withLoading(dispatch, async () => {
                const property = await Services.property.getProperty(propertyId)
                await dispatch("setProperty", property)
            })
        }
    },

    async loadPublicPropertyInfo({ state, dispatch }, propertyId: number) {
        if (state.property?.id != propertyId) {
            await StoreUtils.withLoading(dispatch, async () => {
                const info = await Services.property.getPublicPropertyInfo(propertyId)
                await dispatch("setPublicPropertyInfo", info)
            })
        }
    },

    async loadPropertiesAccesses({ dispatch, state }) {
        if (state.propertiesAccesses !== undefined) return
        await StoreUtils.withLoading(dispatch, async () => {
            const propertiesAccesses = await Services.property.getPropertiesAccesses()
            dispatch("setPropertiesAccesses", propertiesAccesses)
        })
    }
}

//===================================
// Mutators
//===================================
const mutations: MutationTree<PropertiesState> = {
    ...make.mutations(state),

    resetState(state) {
        state.properties = undefined
        state.property = undefined
    }
}

//===================================
// Module
//===================================
export const properties: Module<PropertiesState, RootState> = {
    namespaced,
    state: state,
    getters: getters,
    actions: actions,
    mutations: mutations
}
